import { axios } from 'configs'

export type TSurveyResponseFormat = 'FORM' | 'TALK'
export type TSurveyStatus = 'DRAFT' | 'ACTIVE' | 'CLOSED'
export type TSurveyStatusUpdate = Extract<TSurveyStatus, 'ACTIVE' | 'CLOSED'>
export type TSurveyQuestionType = 'SINGLE' | 'MULTI' | 'TEXT' | 'DATE'
export type TSurveyImageAspectRatio = '1:1' | '16:9'

export type TSurveyListItem = {
  id: number
  name: string
  responseFormat: TSurveyResponseFormat
  status: TSurveyStatus
  questionCount: number
  responseCount: number
  createdAt: string
  updatedAt: string
}

export type TSurveyChoice = {
  id?: number
  label: string
  tagIds?: number[]
}

export type TSurveyQuestion = {
  id?: number
  type: TSurveyQuestionType
  title: string
  required?: boolean
  maxSelections?: number | null
  imageUrl?: string | null
  imageAspectRatio?: TSurveyImageAspectRatio | null
  choices?: TSurveyChoice[]
}

export type TSurveyQuestionInput = Omit<
  TSurveyQuestion,
  'maxSelections'
> & {
  maxSelections?: number
}

export type TSurveyReminder = {
  id: string
  days: number
  time?: string
  message: string
}

export type TSurveyComboRule = {
  tagId: number
  conditions: Array<
    | {
        questionId: number
        choiceId: number
        questionIndex?: never
        choiceIndex?: never
      }
    | {
        questionIndex: number
        choiceIndex: number
        questionId?: never
        choiceId?: never
      }
  >
}

export type TSurveyStatusResult = {
  id: number
  status: TSurveyStatus
  version: number
}

export type TSurvey = {
  id: number
  name: string
  responseFormat: TSurveyResponseFormat
  status: TSurveyStatus
  thanksMessage?: string | null
  completionTagId?: number | null
  responseLimit?: number | null
  reminderEnabled: boolean
  reminders: TSurveyReminder[]
  questions: Array<
    TSurveyQuestion & {
      id: number
      sortOrder: number
      choices?: Array<TSurveyChoice & { id: number; tagIds: number[] }>
    }
  >
  comboRules?: Array<{
    id: number
    tagId: number
    conditions: Array<{ questionId: number; choiceId: number }>
  }>
  droppedComboRuleCount?: number
  version: number
  createdAt: string
  updatedAt: string
}

export type TUpsertSurveyRequest = {
  name: string
  responseFormat?: 'FORM'
  status?: TSurveyStatus
  thanksMessage?: string
  completionTagId?: number | null
  responseLimit?: number | null
  reminderEnabled?: boolean
  reminders?: TSurveyReminder[]
  questions: TSurveyQuestionInput[]
  comboRules?: TSurveyComboRule[]
}

export type TUpdateSurveyRequest = TUpsertSurveyRequest & {
  expectedVersion: number
}

export type TSurveyAggregation = {
  targetCount: number
  startedCount: number
  completedCount: number
  respondentCount: number
  completionRate: number
  questions: Array<{
    questionId: number
    title: string
    type: TSurveyQuestionType
    answeredCount: number
    choiceCounts?: Array<{
      choiceId: number
      label: string
      count: number
      percentage: number
    }>
    answers?: Array<{ id: number; value: string }>
    nextCursor?: number | null
    answerWatermark?: number
  }>
}

export type TSurveyQuestionAnswerPage = {
  answers: Array<{ id: number; value: string }>
  nextCursor: number | null
  answerWatermark: number
}

export type TUserSurveyAnswerHistory = {
  responseId: number
  surveyId: number
  surveyName: string
  startedAt: string
  completedAt: string | null
  answers: Array<{
    questionId: number
    questionTitle: string
    questionType: TSurveyQuestionType
    answeredAt: string
    value: string
  }>
}

export const listSurveys = async (): Promise<TSurveyListItem[]> => {
  const response = await axios.get<TSurveyListItem[]>('/surveys')
  return response.data
}

export const getSurvey = async (surveyId: number): Promise<TSurvey> => {
  const response = await axios.get<TSurvey>(`/surveys/${surveyId}`)
  return response.data
}

export const createSurvey = async (
  request: TUpsertSurveyRequest,
): Promise<TSurvey> => {
  const response = await axios.post<TSurvey>('/surveys', request)
  return response.data
}

export const updateSurvey = async (
  surveyId: number,
  request: TUpdateSurveyRequest,
): Promise<TSurvey> => {
  const response = await axios.put<TSurvey>(`/surveys/${surveyId}`, request)
  return response.data
}

export const deleteSurvey = async (surveyId: number): Promise<boolean> => {
  const response = await axios.delete<boolean>(`/surveys/${surveyId}`)
  return response.data
}

export const copySurvey = async (surveyId: number): Promise<TSurvey> => {
  const response = await axios.post<TSurvey>(`/surveys/${surveyId}/copy`)
  return response.data
}

export const updateSurveyStatus = async (
  surveyId: number,
  status: TSurveyStatusUpdate,
): Promise<TSurveyStatusResult> => {
  const response = await axios.patch<TSurveyStatusResult>(
    `/surveys/${surveyId}/status`,
    { status },
  )
  return response.data
}

export const getSurveyAggregation = async (
  surveyId: number,
): Promise<TSurveyAggregation> => {
  const response = await axios.get<TSurveyAggregation>(
    `/surveys/${surveyId}/aggregation`,
  )
  return response.data
}

export const getSurveyQuestionAnswers = async (
  surveyId: number,
  questionId: number,
  cursor: number,
  answerWatermark: number,
): Promise<TSurveyQuestionAnswerPage> => {
  const response = await axios.get<TSurveyQuestionAnswerPage>(
    `/surveys/${surveyId}/questions/${questionId}/answers`,
    { params: { cursor, answerWatermark } },
  )
  return response.data
}

export const downloadSurveyCsv = async (surveyId: number): Promise<Blob> => {
  const response = await axios.get<Blob>(`/surveys/${surveyId}/export-csv`, {
    responseType: 'blob',
  })
  return response.data
}

export const getUserSurveyAnswers = async (
  userLineId: number,
): Promise<TUserSurveyAnswerHistory[]> => {
  const response = await axios.get<TUserSurveyAnswerHistory[]>(
    `/surveys/user/${userLineId}/answers`,
  )
  return response.data
}
